import { useCallback, useEffect, useRef, useState } from "react";
import { tdApi } from "@src/services/api/tdApi";
import { getApiErrorMessage } from "@src/utils/apiError";
import type { ApiJob } from "@tradingdroid/frontends-types";

const CHILDREN_PAGE_SIZE = 50;

interface UseJobChildrenResult {
  children: ApiJob[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  /** The API returned a `next` link this walk already followed, so paging stopped */
  stalled: boolean;
  loadMore: () => Promise<void>;
}

/**
 * Paged child jobs of a coordinator. A Monte Carlo permutation run spawns hundreds, so children
 * arrive a page at a time rather than all at once.
 *
 * `queryFilter` is an ANTLR query string; changing it or the job restarts paging from the first page.
 */
export const useJobChildren = (
  jobId: number | null,
  queryFilter?: string,
): UseJobChildrenResult => {
  const [children, setChildren] = useState<ApiJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [stalled, setStalled] = useState(false);

  const nextUrlRef = useRef<string | null>(null);
  // Every `next` link this walk has followed. A link served twice means the cursor stopped moving,
  // and following it again would append the same page on every press.
  const followedUrlsRef = useRef(new Set<string>());
  const inFlightRef = useRef(false);
  // Every job or filter change starts a new walk. A fetch captures the walk it started in and writes
  // only while that walk is current, so a slow response cannot land in a later walk, not even one
  // for the same job and filter the operator came back to.
  const requestKey = `${String(jobId)}:${queryFilter ?? ""}`;
  const walkRef = useRef(0);

  const fetchPage = useCallback(
    async (isInitial: boolean) => {
      if (jobId == null) return;
      const myWalk = walkRef.current;
      const isStale = () => walkRef.current !== myWalk;

      // `loading` state lags a click by a render, so two fast "Load more" presses would both pass
      // that check, fetch the same `next` URL, and append the same page twice. The ref flips
      // synchronously, so the second press is dropped.
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      setLoading(true);
      setError(null);
      try {
        if (!isInitial && nextUrlRef.current) followedUrlsRef.current.add(nextUrlRef.current);
        const response =
          !isInitial && nextUrlRef.current
            ? ((await tdApi.getApiUrl(nextUrlRef.current)) as {
                items?: ApiJob[];
                next?: string;
              })
            : await tdApi.getJobChildren(jobId, {
                q: queryFilter || undefined,
                limit: CHILDREN_PAGE_SIZE,
                sort_by: "id:ASC",
              });

        if (isStale()) return;
        const items = response?.items ?? [];
        const repeated = !!response?.next && followedUrlsRef.current.has(response.next);
        nextUrlRef.current = repeated ? null : (response?.next ?? null);
        if (repeated) setStalled(true);
        setChildren((current) => (isInitial ? items : [...current, ...items]));
        setHasMore(!!nextUrlRef.current);
      } catch (err) {
        if (isStale()) return;
        setError(getApiErrorMessage(err));
        // A failed FIRST page means there is nothing to page through. A failed later page keeps
        // `nextUrlRef`, so leaving hasMore alone lets the operator press Load more again instead of
        // losing pagination to one flaky request.
        if (isInitial) setHasMore(false);
      } finally {
        // Only the active generation may release the guard. A request left over from a previous
        // filter must not clear it while this generation's own request is still running.
        if (!isStale()) {
          inFlightRef.current = false;
          setLoading(false);
        }
      }
    },
    [jobId, queryFilter, requestKey],
  );

  useEffect(() => {
    walkRef.current += 1;
    nextUrlRef.current = null;
    followedUrlsRef.current.clear();
    // Any request still in flight belongs to the previous key, and its result is discarded on
    // arrival. Clear the guard so this key's first page is not mistaken for a duplicate press.
    inFlightRef.current = false;
    setChildren([]);
    setHasMore(false);
    setStalled(false);
    fetchPage(true);
    return () => {
      walkRef.current += 1;
    };
  }, [requestKey, fetchPage]);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    await fetchPage(false);
  }, [loading, hasMore, fetchPage]);

  return { children, loading, error, hasMore, stalled, loadMore };
};
